import { type ReactNode, type MouseEvent, memo, useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { twMerge } from 'tailwind-merge'
import { Checkbox } from '@highlight/ui'
import {
  type HistorySelectItem,
  historySelectionKey,
  isHistoryItemSelectedAtom,
  toggleHistorySelectedItemAtom,
} from '@renderer/overlay/Sidepanel/stores/history-select'
import { useHistorySelectContext } from './history-select-context'

interface HistorySelectCheckboxProps {
  item: HistorySelectItem
  icon: ReactNode
  index: number
}

export const HistorySelectCheckbox = memo(function HistorySelectCheckbox({
  item,
  icon,
  index,
}: HistorySelectCheckboxProps) {
  const toggleItem = useSetAtom(toggleHistorySelectedItemAtom)
  const { getLastSelectedIndex, setLastSelectedIndex, handleRangeSelect } = useHistorySelectContext()

  const isSelected = useAtomValue(isHistoryItemSelectedAtom(historySelectionKey(item)))

  const handleCheck = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()

      const lastSelectedIndex = getLastSelectedIndex()
      if (e.shiftKey && lastSelectedIndex !== null) {
        handleRangeSelect(lastSelectedIndex, index)
      } else {
        toggleItem({ id: item.id, type: item.type })
        setLastSelectedIndex(index)
      }
    },
    [item.id, item.type, index, getLastSelectedIndex, handleRangeSelect, toggleItem, setLastSelectedIndex],
  )

  return (
    <div className="relative flex size-5 shrink-0 items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div
        className={twMerge(
          'pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity',
          isSelected ? 'opacity-0' : 'opacity-100 group-hover/cell:opacity-0',
        )}
      >
        {icon}
      </div>
      <Checkbox
        checked={isSelected}
        onClick={handleCheck}
        className={twMerge(
          'absolute cursor-pointer',
          "after:absolute after:-inset-x-2 after:-inset-y-3.5 after:-ml-1 after:content-['']",
          isSelected ? 'opacity-100' : 'opacity-0 group-hover/cell:opacity-100',
        )}
        tabIndex={-1}
      >
        <Checkbox.Indicator />
      </Checkbox>
    </div>
  )
})
